import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import useStyles from "./TextAnswerRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import { QuestionType } from "../../../models/enums/QuestionType";
import { removeHtmlTags } from "../../../utils/index";
import { QuestionModel } from "../../../models/QuestionModel";
import InputBox from "../../shared/InputBox";
import useQuestion from "../../../hooks/useQuestion";
import useTranslatable from "../../../hooks/useTranslatable";
import { getSettingString } from "../../../models/utilities/settingUtilities";
import { translate } from "../../../models/utilities/translationUtilities";
import { size } from "../../../models/utilities/questionUtilities";
import {
    getAriaLabelledByFromId,
    getStandaloneInputAriaLabelledBy,
    useAriaLabelByForGridRenderedInputFieldQuestion,
    useAriaLabelledByForInputFieldQuestion,
} from "../../../utils/WCAGHelper";
import { useHybridGridSubHeaderAria } from "./HybridGridSubHeaderAriaContext";
import { useKeyboardNavigation } from "../../../hooks/useKeyboardNavigation";
import { TextAnswerFieldNavigation } from "../../controls/KeyboardNavigation/TextAnswerFieldNavigation";
import useQuestionnaire from "../../../hooks/useQuestionnaire";
import { useRunningTotalFirstVariableCellCoords } from "../../../hooks/RunningTotalFirstVariableCellContext";
import { useGridCellValidationError } from "../../../hooks/GridCellValidationErrorContext";
import { isAnswerRequired } from "../../../hooks/useValidation";
import { runningTotalCellReduxHasError } from "../../../models/utilities/gridUtilities";
import { useSelector } from "react-redux";
import { StoreState } from "../../../models/states/StoreState";
import { shouldUseModernTheme } from "../../../theme/navigationButtonThemeColors";
import { shouldSuppressModernHybridGridInputWrapperMargin } from "../../../theme/gridThemeTokens";
import type { Theme } from "../../../types/Theme";

interface Props extends RendererProps {
    multiline?: boolean;
    inputType?: string;
    placeholder?: string;
    inputDivRef?: React.LegacyRef<HTMLDivElement>;
    inputRef?: React.LegacyRef<HTMLInputElement & HTMLTextAreaElement>;
    min?: number;
    max?: number;
    maxLength?: number;
    onBlur?: React.FocusEventHandler;
    onFocus?: React.FocusEventHandler;
    onKeyDown?: React.KeyboardEventHandler;
    onKeyPress?: React.KeyboardEventHandler;
    onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    onPaste?: (pastedText: string) => void;
    /** Called with the autofilled value when the browser autofills without triggering React's onBlur/onChange (React 17+). */
    onAutoFill?: (value: string) => void;
    openDatePicker?(): void;
    value?: string;
    disabled?: boolean;
    lines?: number;
    inputMode?: "search" | "text" | "tel" | "none" | "url" | "email" | "numeric" | "decimal";
    isProbQuestion?: boolean;
    probQuestionId?: string;
    ariaLabelledBy?: string;
}

function hasDatePicker(question: QuestionModel) {
    return ["Date", "DateTime", "Time"].includes(getSettingString(question, "Type"));
}

export default function TextAnswerRenderer(props: Props) {
    const {
        gridRendering,
        multiline,
        inputType,
        placeholder,
        min,
        max,
        maxLength,
        onBlur,
        onFocus,
        onKeyDown: propsOnKeyDown,
        onKeyPress,
        onChange,
        onPaste,
        onAutoFill,
        openDatePicker,
        theme,
        language,
        value,
        disabled,
        lines,
        inputMode,
        gridRenderedColumnIndex,
        gridRenderedRowIndex,
        isProbQuestion,
        probQuestionId,
        ariaLabelledBy: ariaLabelledByOverride,
        inputRef: inputRefFromParent,
    } = props;

    const jssTheme = useTheme() as Theme;
    const classes = useStyles({ theme: jssTheme, gridRendering });
    const useModernTheme = shouldUseModernTheme(jssTheme);
    const question = useQuestion();
    const disableTextareaResize =
        useModernTheme && question?.type === QuestionType.OpenEndAnswer;
    const firstRtVariableCellCoords = useRunningTotalFirstVariableCellCoords();
    const parentIsRunningTotal = question?.parent?.type === QuestionType.RunningTotal;
    const isFirstGridInputForParentQuestion = React.useMemo(() => {
        if (!gridRendering || !parentIsRunningTotal) {
            return undefined;
        }
        if (gridRenderedRowIndex === undefined || gridRenderedColumnIndex === undefined) {
            return undefined;
        }
        if (firstRtVariableCellCoords === undefined) {
            return undefined;
        }
        if (firstRtVariableCellCoords === null) {
            return true;
        }
        return (
            gridRenderedRowIndex === firstRtVariableCellCoords.rowIndex &&
            gridRenderedColumnIndex === firstRtVariableCellCoords.colIndex
        );
    }, [
        gridRendering,
        parentIsRunningTotal,
        firstRtVariableCellCoords,
        gridRenderedRowIndex,
        gridRenderedColumnIndex,
    ]);
    const questionnaire = useQuestionnaire();
    const isRequired = isAnswerRequired(question, questionnaire);
    const validation = useSelector((state: StoreState) => state.interview.validation);
    const gridCellValidationError = useGridCellValidationError();
    const questionText = useTranslatable(question, "TEXT");
    const leftLabel = useTranslatable(question, "LeftLabel");
    const rightLabel = useTranslatable(question, "RightLabel");
    const questionId = question?.parent?.elementId ?? question?.elementId;
    const questionType = question?.parent?.type ?? question?.type;

    const [variable] = question.variables;
    const hasValidationError =
        parentIsRunningTotal && gridRendering
            ? gridCellValidationError !== undefined
                ? gridCellValidationError
                : runningTotalCellReduxHasError(
                      variable?.name,
                      question?.parent,
                      validation?.errorsByVariableName,
                      value,
                  )
            : Boolean(
                  variable?.name &&
                      validation?.errorsByVariableName?.[variable.name]?.length > 0,
              );
    const validationErrorDescribedById =
        parentIsRunningTotal && question?.parent?.elementId
            ? question.parent.elementId
            : question.elementId;

    const useAriaLabelledBy = useAriaLabelledByForInputFieldQuestion(question);
    const isGridRenderedLabellingInputBox =
        useAriaLabelByForGridRenderedInputFieldQuestion(question);

    const isHybridGridRenderedTextFieldTypeQuestion =
        gridRendering &&
        questionType === QuestionType.HybridGrid &&
        (question.type === QuestionType.NumericAnswer || question.type === QuestionType.TextAnswer);

    const includeHybridGridSubHeaderChrome = useHybridGridSubHeaderAria();

    // Default should be aria-label if not grid rendered and aria-labelled by for variable type in grid
    const useAriaLabel = !useAriaLabelledBy && !gridRendering && isGridRenderedLabellingInputBox;

    const classNames = classnames(
        classes.textAnswer,
        multiline && classes.textAnswerMultiLine,
        question.type === QuestionType.NumericAnswer && classes.textAnswerNumeric,
        question.type === QuestionType.DateTimeAnswer && classes.textAnswerDateTime,
    );

    const accessory: IconProp = hasDatePicker(question) ? ["far", "calendar-alt"] : null;
    const openCalendarLabel = translate(theme, "OpenCalendar", language, "Open calendar");

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (onPaste && e.clipboardData) {
            onPaste(e.clipboardData.getData("text/plain"));
        }
    };

    const ariaAttributes = {};
    if (ariaLabelledByOverride) {
        ariaAttributes["aria-labelledby"] = ariaLabelledByOverride;
    } else if (useAriaLabelledBy && !isHybridGridRenderedTextFieldTypeQuestion) {
        // DEV-98353: standalone fields need TITLE + LABEL; grid cells and choice other-specify
        // boxes must keep their existing chrome-id behavior, hence the !gridRendering first-line
        // guard plus the helper's own type+no-parent self-guard (see WCAGHelper.ts).
        ariaAttributes["aria-labelledby"] =
            (!gridRendering && getStandaloneInputAriaLabelledBy(question, language)) ||
            getAriaLabelledByFromId(question, questionId);
    } else if (useAriaLabel) {
        const questionAriaText = removeHtmlTags(questionText);
        if (questionAriaText) {
            ariaAttributes["aria-label"] = questionAriaText;
        }
    } else if (!isGridRenderedLabellingInputBox || isHybridGridRenderedTextFieldTypeQuestion) {
        ariaAttributes["aria-labelledby"] = getAriaLabelledByFromId(
            question,
            questionId,
            `${questionType}_INPUTBOX`,
            gridRenderedColumnIndex + 1,
            gridRenderedRowIndex + 1,
            undefined,
            language,
            isFirstGridInputForParentQuestion,
            includeHybridGridSubHeaderChrome,
        );
    }

    const aiProbeVariable: { elementId?: string; id?: string } = {};
    if (isProbQuestion) {
        const probeSuffix = probQuestionId ? `_AI_PROBE_${probQuestionId}` : "_AI_PROBE";
        aiProbeVariable["elementId"] = `${variable.elementId}${probeSuffix}`;
        aiProbeVariable["id"] = `${variable.id}${probeSuffix}`;
    }

    const elementId = isProbQuestion ? aiProbeVariable.elementId : variable.elementId;
    const { elementReference, onKeyDown: keyboardNavOnKeyDown } = useKeyboardNavigation(
        `${elementId}_INPUT`,
        isProbQuestion ? aiProbeVariable : variable,
        question,
        TextAnswerFieldNavigation,
        null,
        isProbQuestion,
    );

    const setRef = React.useCallback(
        (el: HTMLInputElement | HTMLTextAreaElement | null) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (elementReference as any).current = el;
            if (inputRefFromParent) {
                if (typeof inputRefFromParent === "function") {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (inputRefFromParent as any)(el);
                } else {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (inputRefFromParent as any).current = el;
                }
            }
        },
        [inputRefFromParent],
    );

    // Detect browser autofill and notify the parent via onAutoFill
    const mountReactValueRef = React.useRef(value);
    React.useEffect(() => {
        if (!onAutoFill) return;
        const el = elementReference.current;
        if (!el) return;

        const isAutofilled = (target: HTMLElement) => {
            try {
                return target.matches(":-webkit-autofill");
            } catch {
                return false;
            }
        };

        // Catch autofill that happened before this effect ran.
        const domValueAtMount = el.value ?? "";
        if (
            isAutofilled(el) &&
            domValueAtMount &&
            domValueAtMount !== (mountReactValueRef.current ?? "")
        ) {
            onAutoFill(domValueAtMount);
        }

        // Catch autofill that happens after mount (user picks from the autofill dropdown).
        // The native 'input' event fires even though React 17's delegation misses it.
        const handleInput = (e: Event) => {
            const target = e.target as HTMLInputElement | HTMLTextAreaElement;
            if (target && (isAutofilled(target) || document.activeElement !== target)) {
                // Any input while the target element is not focused is considered autofill
                // if the the target element is focused, value is going to be updated onBlur
                onAutoFill(target.value ?? "");
            }
        };

        el.addEventListener("input", handleInput);
        return () => el.removeEventListener("input", handleInput);
    }, [onAutoFill]);

    // Combine keyboard navigation handler with custom onKeyDown prop
    const combinedOnKeyDown = (event: React.KeyboardEvent) => {
        // First call the custom onKeyDown handler if provided
        if (propsOnKeyDown) {
            propsOnKeyDown(event);
        }
        // Keep native Tab sequence for date/time inputs so focus can move to the calendar trigger.
        if (
            !event.defaultPrevented &&
            event.key === "Tab" &&
            hasDatePicker(question) &&
            !gridRendering
        ) {
            return;
        }
        // Then call keyboard navigation handler if event wasn't prevented
        if (!event.defaultPrevented && keyboardNavOnKeyDown) {
            keyboardNavOnKeyDown(event);
        }
    };

    return (
        <InputBox
            freeform={multiline}
            size={size(question)}
            leftLabel={leftLabel}
            rightLabel={rightLabel}
            className={classNames}
            accessoryRight={accessory}
            onAccessoryRightClick={openDatePicker && !disabled ? () => openDatePicker() : undefined}
            accessoryRightAriaLabel={openDatePicker ? openCalendarLabel : undefined}
            disabled={disabled}
            disableTextareaResize={disableTextareaResize}
            suppressWrapperTopMargin={shouldSuppressModernHybridGridInputWrapperMargin(
                jssTheme,
                Boolean(gridRendering && questionType === QuestionType.HybridGrid),
            )}
        >
            {multiline && (
                <textarea
                    id={`${elementId}_INPUT`}
                    {...ariaAttributes}
                    name={variable.elementId}
                    rows={lines || 2}
                    placeholder={placeholder}
                    disabled={disabled}
                    ref={setRef}
                    maxLength={maxLength}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onKeyDown={combinedOnKeyDown}
                    onKeyPress={onKeyPress}
                    onChange={onChange}
                    onPaste={handlePaste}
                    value={value ?? undefined}
                    aria-required={isRequired}
                    aria-invalid={hasValidationError || undefined}
                    aria-describedby={
                        hasValidationError ? `${validationErrorDescribedById}_ERROR` : undefined
                    }
                />
            )}
            {!multiline && (
                <input
                    type={inputType}
                    inputMode={inputMode}
                    id={`${elementId}_INPUT`}
                    {...ariaAttributes}
                    name={variable.elementId}
                    placeholder={placeholder}
                    ref={setRef}
                    min={min}
                    max={max}
                    maxLength={maxLength}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onKeyDown={combinedOnKeyDown}
                    onKeyPress={onKeyPress}
                    onChange={onChange}
                    onPaste={handlePaste}
                    disabled={disabled}
                    value={value ?? undefined}
                    autoComplete={inputType === "email" ? "email" : inputType === "tel" ? "tel" : "off"}
                    aria-required={isRequired}
                    aria-invalid={hasValidationError || undefined}
                    aria-describedby={
                        hasValidationError ? `${validationErrorDescribedById}_ERROR` : undefined
                    }
                    style={{
                        textAlign: inputMode ? "right" : "inherit",
                    }}
                />
            )}
        </InputBox>
    );
}
